import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { authFetch } from '../../utils/authFetch';
import Footer from '../../components/Footer';

const DIAS = [
    { value: 1, label: 'Segunda' },
    { value: 2, label: 'Terça' },
    { value: 3, label: 'Quarta' },
    { value: 4, label: 'Quinta' },
    { value: 5, label: 'Sexta' },
];

const DIAS_SHORT = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex'];

const TURNOS = [
    { value: 0, label: 'Manhã' },
    { value: 1, label: 'Tarde' },
];

const SHIFT_LABELS = { 0: 'Manhã', 1: 'Tarde', 2: 'Dia inteiro' };

const getMondayOfWeek = (offset = 0) => {
    const today = new Date();
    const day = today.getDay();
    const diff = (day === 0 ? -6 : 1 - day);
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff + offset * 7);
    monday.setHours(0, 0, 0, 0);
    return monday;
};

const toISODate = (date) => date.toISOString().split('T')[0];

const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [, m, d] = dateStr.split('-');
    return `${d}/${m}`;
};

const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
};

// Exibe "HH:mm" a partir de string "HH:mm" ou "HH:mm:ss"
const fmt = (t) => (t ? t.substring(0, 5) : '');

const EscalaTerapeuta = () => {
    const { id: therapistId } = useParams();
    const { showNotification } = useNotification();
    const lidRef = useRef(0);
    const nextLid = () => ++lidRef.current;

    const [terapeuta, setTerapeuta] = useState(null);
    // slots: [{ dayOfWeek, shift, startTime, endTime, _lid }]
    const [slots, setSlots] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [loadingEscala, setLoadingEscala] = useState(true);
    const [savingEscala, setSavingEscala] = useState(false);

    const [weekOffset, setWeekOffset] = useState(0);
    const [showAllBlocks, setShowAllBlocks] = useState(false);
    const [showAddBlock, setShowAddBlock] = useState(false);
    const [novoBloco, setNovoBloco] = useState({ startDate: '', endDate: '', shift: 2, startTime: '', endTime: '', reason: '' });
    const [savingBloco, setSavingBloco] = useState(false);
    const [confirmDeleteBlockId, setConfirmDeleteBlockId] = useState(null);
    const [confirmDeleteSlotLid, setConfirmDeleteSlotLid] = useState(null);

    // Estado para adicionar período: qual dia está com form aberto
    const [addingDay, setAddingDay] = useState(null);
    const [newPeriod, setNewPeriod] = useState({ shift: 0, startTime: '08:00', endTime: '12:00' });

    const today = toISODate(new Date());

    const monday = getMondayOfWeek(weekOffset);
    const weekDays = DIAS.map((dia, i) => {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        return { ...dia, date: toISODate(d) };
    });

    const weekLabel = (() => {
        const start = weekDays[0].date;
        const end = weekDays[4].date;
        if (weekOffset === 0) return `Esta semana (${formatDisplayDate(start)} – ${formatDisplayDate(end)})`;
        if (weekOffset === 1) return `Próxima semana (${formatDisplayDate(start)} – ${formatDisplayDate(end)})`;
        if (weekOffset === -1) return `Semana passada (${formatDisplayDate(start)} – ${formatDisplayDate(end)})`;
        return `${formatDisplayDate(start)} – ${formatDisplayDate(end)}`;
    })();

    useEffect(() => {
        setLoadingEscala(true);
        Promise.all([
            authFetch(`therapist/${therapistId}`).then(r => r.json()),
            authFetch(`therapist-schedule/${therapistId}`).then(r => r.json()),
            authFetch(`therapist-schedule/${therapistId}/blocks`).then(r => r.json()),
        ])
            .then(([terap, escala, blocos]) => {
                setTerapeuta(terap);
                setSlots(escala.map(s => ({ ...s, _lid: nextLid() })));
                setBlocks(blocos);
            })
            .catch(() => showNotification('danger', 'Erro ao carregar escala'))
            .finally(() => setLoadingEscala(false));
    }, [therapistId]);

    const validarHorarioTurno = (shift, startTime, endTime) => {
        if (!startTime || !endTime) return true;
        if (shift === 0 && (startTime > '12:00' || endTime > '12:00'))
            return 'Manhã deve estar entre 00:00 e 12:00.';
        if (shift === 1 && (startTime < '12:01' || endTime < '12:01'))
            return 'Tarde deve estar entre 12:01 e 23:59.';
        return true;
    };

    const addPeriod = async (dayOfWeek) => {
        if (!newPeriod.startTime || !newPeriod.endTime) return;
        const erroHorario = validarHorarioTurno(newPeriod.shift, newPeriod.startTime, newPeriod.endTime);
        if (erroHorario !== true) { showNotification('danger', erroHorario); return; }
        const novosSlots = [...slots, {
            dayOfWeek,
            shift: newPeriod.shift,
            startTime: newPeriod.startTime,
            endTime: newPeriod.endTime,
            _lid: nextLid(),
        }];
        setSlots(novosSlots);
        setNewPeriod({ shift: 0, startTime: '08:00', endTime: '12:00' });
        setAddingDay(null);
        setSavingEscala(true);
        try {
            const body = novosSlots.map(({ dayOfWeek: d, shift, startTime, endTime }) => ({
                dayOfWeek: d, shift, startTime: startTime || null, endTime: endTime || null,
            }));
            const r = await authFetch(`therapist-schedule/${therapistId}`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            if (!r.ok) throw new Error();
            showNotification('success', 'Período adicionado!');
        } catch {
            showNotification('danger', 'Erro ao salvar escala');
        } finally {
            setSavingEscala(false);
        }
    };

    const removePeriod = async (lid) => {
        const novosSlots = slots.filter(s => s._lid !== lid);
        setSlots(novosSlots);
        setSavingEscala(true);
        try {
            const body = novosSlots.map(({ dayOfWeek, shift, startTime, endTime }) => ({
                dayOfWeek, shift, startTime: startTime || null, endTime: endTime || null,
            }));
            const r = await authFetch(`therapist-schedule/${therapistId}`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            if (!r.ok) throw new Error();
            showNotification('success', 'Período removido!');
        } catch {
            showNotification('danger', 'Erro ao remover período');
        } finally {
            setSavingEscala(false);
        }
    };

    const salvarEscala = async () => {
        if (!therapistId) return;
        setSavingEscala(true);
        try {
            const body = slots.map(({ dayOfWeek, shift, startTime, endTime }) => ({
                dayOfWeek,
                shift,
                startTime: startTime || null,
                endTime: endTime || null,
            }));
            const r = await authFetch(`therapist-schedule/${therapistId}`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            if (!r.ok) throw new Error();
            showNotification('success', 'Escala salva com sucesso!');
        } catch {
            showNotification('danger', 'Erro ao salvar escala');
        } finally {
            setSavingEscala(false);
        }
    };

    const adicionarBloco = async (e) => {
        e.preventDefault();
        const shift = Number(novoBloco.shift);
        if (shift !== 2) {
            const erroHorario = validarHorarioTurno(shift, novoBloco.startTime, novoBloco.endTime);
            if (erroHorario !== true) { showNotification('danger', erroHorario); return; }
        }
        setSavingBloco(true);
        try {
            const r = await authFetch(`therapist-schedule/${therapistId}/blocks`, {
                method: 'POST',
                body: JSON.stringify({
                    startDate: novoBloco.startDate,
                    endDate: novoBloco.endDate || novoBloco.startDate,
                    shift,
                    startTime: (shift !== 2 && novoBloco.startTime) ? novoBloco.startTime : null,
                    endTime: (shift !== 2 && novoBloco.endTime) ? novoBloco.endTime : null,
                    reason: novoBloco.reason || null,
                }),
            });
            if (!r.ok) throw new Error();
            const criado = await r.json();
            setBlocks(prev => [...prev, criado].sort((a, b) => a.startDate.localeCompare(b.startDate)));
            setNovoBloco({ startDate: '', endDate: '', shift: 2, startTime: '', endTime: '', reason: '' });
            setShowAddBlock(false);
            showNotification('success', 'Bloqueio adicionado!');
        } catch {
            showNotification('danger', 'Erro ao adicionar bloqueio');
        } finally {
            setSavingBloco(false);
        }
    };

    const excluirBloco = async (blockId) => {
        try {
            const r = await authFetch(`therapist-schedule/blocks/${blockId}`, { method: 'DELETE' });
            if (!r.ok) throw new Error();
            setBlocks(prev => prev.filter(b => b.id !== blockId));
            showNotification('success', 'Bloqueio removido!');
        } catch {
            showNotification('danger', 'Erro ao remover bloqueio');
        }
    };

    // Retorna todos os bloqueios que se sobrepõem ao período
    const getBlocksForPeriod = (dateStr, shift, periodStart, periodEnd) =>
        blocks.filter(b => {
            if (dateStr < b.startDate || dateStr > b.endDate) return false;
            if (b.shift !== 2 && b.shift !== shift) return false;
            if (b.startTime && b.endTime && periodStart && periodEnd)
                return periodStart < b.endTime && periodEnd > b.startTime;
            return true;
        });

    // Divide um período em segmentos livres/bloqueados
    const splitPeriod = (pStart, pEnd, periodBlocks) => {
        // Sem horários definidos: retorna período inteiro com ou sem bloqueio
        if (!pStart || !pEnd) {
            const b = periodBlocks.find(b => !b.startTime || !b.endTime) || periodBlocks[0] || null;
            return [{ start: null, end: null, block: b }];
        }
        // Bloqueio sem horário específico cobre o período inteiro
        const fullBlock = periodBlocks.find(b => !b.startTime || !b.endTime);
        if (fullBlock) return [{ start: pStart, end: pEnd, block: fullBlock }];

        const timedBlocks = [...periodBlocks.filter(b => b.startTime && b.endTime)]
            .sort((a, b) => a.startTime.localeCompare(b.startTime));

        if (timedBlocks.length === 0) return [{ start: pStart, end: pEnd, block: null }];

        const segments = [];
        let cur = pStart;
        for (const b of timedBlocks) {
            const bStart = b.startTime > pStart ? b.startTime : pStart;
            const bEnd   = b.endTime   < pEnd   ? b.endTime   : pEnd;
            if (cur < bStart) segments.push({ start: cur, end: bStart, block: null });
            if (bStart < bEnd) segments.push({ start: bStart, end: bEnd, block: b });
            if (bEnd > cur) cur = bEnd;
        }
        if (cur < pEnd) segments.push({ start: cur, end: pEnd, block: null });
        return segments;
    };

    const isInSchedule = (dayOfWeek, shift) =>
        slots.some(s => s.dayOfWeek === dayOfWeek && s.shift === shift);

    const getPeriodsForDayShift = (dayOfWeek, shift) =>
        slots.filter(s => s.dayOfWeek === dayOfWeek && s.shift === shift);

    const upcomingBlocks = blocks.filter(b => b.endDate >= today);
    const pastBlocks = blocks.filter(b => b.endDate < today);
    const visibleBlocks = showAllBlocks ? blocks : upcomingBlocks;

    return (
        <div className="content">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <Link to="/Terapeutas/Escala" className="text-secondary text-xs">
                        <i className="bi bi-arrow-left me-1"></i>Todas as escalas
                    </Link>
                    <h3 className="font-weight-bolder mb-0 mt-1">
                        {terapeuta ? terapeuta.name : 'Escala de Trabalho'}
                    </h3>
                </div>
            </div>

            {loadingEscala ? (
                <p className="text-sm text-secondary px-1">Carregando...</p>
            ) : (
                <>
                    {/* Escala semanal recorrente — horizontal */}
                    <div className="card mb-3">
                        <div className="card-body p-3">
                            <h6 className="text-uppercase text-secondary text-sm font-weight-bolder mb-3">
                                Escala Semanal Recorrente
                            </h6>

                            <div className="d-flex gap-3">
                                {DIAS.map(dia => {
                                    const diaSlots = slots.filter(s => s.dayOfWeek === dia.value);
                                    const isAddingThisDia = addingDay === dia.value;

                                    return (
                                        <div key={dia.value} className="flex-fill border rounded p-2" style={{ minWidth: 0 }}>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <span className="text-sm font-weight-bold text-uppercase text-secondary">{dia.label}</span>
                                                <button
                                                    className={`btn btn-sm py-0 px-1 ${isAddingThisDia ? 'btn-outline-secondary' : 'btn-primary'}`}
                                                    onClick={() => {
                                                        setAddingDay(isAddingThisDia ? null : dia.value);
                                                        setNewPeriod({ shift: 0, startTime: '08:00', endTime: '12:00' });
                                                    }}
                                                >
                                                    {isAddingThisDia
                                                        ? <i className="bi bi-x"></i>
                                                        : <strong>+</strong>
                                                    }
                                                </button>
                                            </div>

                                            {diaSlots.length === 0 && !isAddingThisDia && (
                                                <div className="text-secondary fst-italic text-xs">—</div>
                                            )}

                                            {diaSlots.map(slot => (
                                                <div key={slot._lid} className="d-flex align-items-center justify-content-between mb-1 gap-1">
                                                    <div className="d-flex align-items-center gap-1 flex-wrap" style={{ minWidth: 0 }}>
                                                        <span className="badge" style={{
                                                            background: slot.shift === 0 ? '#86efac' : '#16a34a',
                                                            color: slot.shift === 0 ? '#166534' : '#fff',
                                                            fontSize: '0.75rem',
                                                        }}>
                                                            {slot.shift === 0 ? 'Manhã' : 'Tarde'}
                                                        </span>
                                                        <span className="text-secondary text-xs">
                                                            {slot.startTime && slot.endTime
                                                                ? `${fmt(slot.startTime)}–${fmt(slot.endTime)}`
                                                                : <em>sem horário</em>
                                                            }
                                                        </span>
                                                    </div>
                                                    {confirmDeleteSlotLid === slot._lid ? (
                                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                                            <span className="text-xs text-secondary">Excluir?</span>
                                                            <button
                                                                className="btn btn-danger btn-sm py-0 px-2"
                                                                onClick={() => { removePeriod(slot._lid); setConfirmDeleteSlotLid(null); }}
                                                            >
                                                                Sim
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm py-0 px-2"
                                                                onClick={() => setConfirmDeleteSlotLid(null)}
                                                            >
                                                                Não
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            className="btn btn-link p-0 flex-shrink-0"
                                                            title="Remover"
                                                            onClick={() => setConfirmDeleteSlotLid(slot._lid)}
                                                        >
                                                            <i className="bi bi-x-circle text-danger"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            ))}

                                            {isAddingThisDia && (
                                                <div className="mt-1">
                                                    <select
                                                        className="form-select form-select-sm mb-1"
                                                        value={newPeriod.shift}
                                                        onChange={e => {
                                                            const s = Number(e.target.value);
                                                            setNewPeriod(p => ({
                                                                ...p,
                                                                shift: s,
                                                                startTime: s === 0 ? '08:00' : '14:00',
                                                                endTime: s === 0 ? '12:00' : '18:00',
                                                            }));
                                                        }}
                                                    >
                                                        <option value={0}>Manhã</option>
                                                        <option value={1}>Tarde</option>
                                                    </select>
                                                    <input
                                                        type="time"
                                                        className="form-control form-control-sm mb-1"
                                                        value={newPeriod.startTime}
                                                        min={newPeriod.shift === 1 ? '12:01' : '00:00'}
                                                        max={newPeriod.shift === 0 ? '12:00' : '23:59'}
                                                        onChange={e => setNewPeriod(p => ({ ...p, startTime: e.target.value }))}
                                                    />
                                                    <input
                                                        type="time"
                                                        className="form-control form-control-sm mb-1"
                                                        value={newPeriod.endTime}
                                                        min={newPeriod.shift === 1 ? '12:01' : '00:00'}
                                                        max={newPeriod.shift === 0 ? '12:00' : '23:59'}
                                                        onChange={e => setNewPeriod(p => ({ ...p, endTime: e.target.value }))}
                                                    />
                                                    <button
                                                        className="btn btn-primary w-100"
                                                        onClick={() => addPeriod(dia.value)}
                                                        disabled={!newPeriod.startTime || !newPeriod.endTime || savingEscala}
                                                    >
                                                        <i className="bi bi-check me-1"></i>Adicionar
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    </div>

                    {/* Visão semanal */}
                    <div className="card mb-3">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="text-uppercase text-secondary text-xs font-weight-bolder mb-0">
                                            {weekLabel}
                                        </h6>
                                        <div className="d-flex gap-1">
                                            <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setWeekOffset(w => w - 1)}>
                                                <i className="bi bi-chevron-left"></i>
                                            </button>
                                            {weekOffset !== 0 && (
                                                <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setWeekOffset(0)}>
                                                    Hoje
                                                </button>
                                            )}
                                            <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => setWeekOffset(w => w + 1)}>
                                                <i className="bi bi-chevron-right"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="row g-2">
                                        {weekDays.map(dia => {
                                            const isToday = dia.date === today;
                                            const dayHasSchedule = TURNOS.some(t => isInSchedule(dia.value, t.value));
                                            return (
                                                <div key={dia.value} className="col">
                                                    <div className={`border rounded p-2 text-center h-100 ${isToday ? 'border-primary' : ''}`}
                                                        style={{ minWidth: 60 }}>
                                                        <div className={`text-sm font-weight-bold mb-1 ${isToday ? 'text-primary' : 'text-secondary'}`}>
                                                            {DIAS_SHORT[dia.value]}
                                                        </div>
                                                        <div className={`text-xs mb-2 ${isToday ? 'text-primary' : 'text-secondary'}`}>
                                                            {formatDisplayDate(dia.date)}
                                                        </div>
                                                        {TURNOS.map(turno => {
                                                            const periodos = getPeriodsForDayShift(dia.value, turno.value);
                                                            if (periodos.length === 0) return null;
                                                            return (
                                                                <div key={turno.value} className="mb-1">
                                                                    {periodos.map(p => {
                                                                        const periodBlocks = getBlocksForPeriod(dia.date, turno.value, p.startTime, p.endTime);
                                                                        const segments = splitPeriod(p.startTime, p.endTime, periodBlocks);
                                                                        return segments.map((seg, i) => {
                                                                            const label = seg.start && seg.end
                                                                                ? `${fmt(seg.start)}–${fmt(seg.end)}`
                                                                                : (p.startTime && p.endTime ? `${fmt(p.startTime)}–${fmt(p.endTime)}` : turno.label);
                                                                            return seg.block ? (
                                                                                <span
                                                                                    key={`${p._lid}-${i}`}
                                                                                    className="badge bg-danger d-block mb-1"
                                                                                    title={seg.block.reason || 'Bloqueado'}
                                                                                    style={{ fontSize: '0.7rem', cursor: 'default' }}
                                                                                >
                                                                                    <i className="bi bi-lock-fill me-1"></i>
                                                                                    {label}
                                                                                </span>
                                                                            ) : (
                                                                                <span key={`${p._lid}-${i}`} className="badge d-block mb-1" style={{
                                                                                    fontSize: '0.7rem',
                                                                                    background: turno.value === 0 ? '#86efac' : '#16a34a',
                                                                                    color: turno.value === 0 ? '#166534' : '#fff',
                                                                                }}>
                                                                                    {label}
                                                                                </span>
                                                                            );
                                                                        });
                                                                    })}
                                                                </div>
                                                            );
                                                        })}
                                                        {!dayHasSchedule && (
                                                            <span className="text-secondary text-xs">—</span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Alertas de bloqueios na semana */}
                                    {(() => {
                                        const alertas = weekDays.flatMap(dia =>
                                            TURNOS.flatMap(t =>
                                                getPeriodsForDayShift(dia.value, t.value).flatMap(p => {
                                                    const periodBlocks = getBlocksForPeriod(dia.date, t.value, p.startTime, p.endTime);
                                                    return splitPeriod(p.startTime, p.endTime, periodBlocks)
                                                        .filter(seg => seg.block)
                                                        .map((seg, i) => {
                                                            const label = seg.start && seg.end
                                                                ? `${fmt(seg.start)}–${fmt(seg.end)}`
                                                                : (p.startTime && p.endTime ? `${fmt(p.startTime)}–${fmt(p.endTime)}` : t.label);
                                                            return { dia, label, block: seg.block, key: `${dia.date}-${p._lid}-${i}` };
                                                        });
                                                })
                                            )
                                        );
                                        if (alertas.length === 0) return null;
                                        return (
                                            <div className="mt-3">
                                                {alertas.map(({ dia, label, block, key }) => (
                                                    <div key={key}
                                                        className="d-flex align-items-center gap-2 text-xs text-danger mb-1">
                                                        <i className="bi bi-exclamation-triangle-fill"></i>
                                                        <span>
                                                            <strong>{DIAS_SHORT[dia.value]} {formatDisplayDate(dia.date)}</strong> — {label}
                                                            {block.reason && <span className="text-secondary"> · {block.reason}</span>}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    })()}
                        </div>
                    </div>

                    {/* Bloqueios */}
                    <div className="card mb-4">
                        <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="text-uppercase text-secondary text-xs font-weight-bolder mb-0">
                                    Bloqueios de Agenda
                                    {upcomingBlocks.length > 0 && (
                                        <span className="badge bg-danger ms-2">{upcomingBlocks.length}</span>
                                    )}
                                </h6>
                                <button
                                    className="btn btn-info btn-sm"
                                    onClick={() => setShowAddBlock(v => !v)}
                                >
                                    <i className={`bi ${showAddBlock ? 'bi-x' : 'bi-plus-lg'} me-1`}></i>
                                    {showAddBlock ? 'Cancelar' : 'Novo Bloqueio'}
                                </button>
                            </div>

                            {showAddBlock && (
                                <form onSubmit={adicionarBloco} className="bg-light rounded p-3 mb-3">
                                    <div className="row g-2 align-items-end">
                                        <div className="col-auto">
                                            <label className="form-label text-xs mb-1">Data início</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={novoBloco.startDate}
                                                min={today}
                                                onChange={e => setNovoBloco(p => ({ ...p, startDate: e.target.value }))}
                                                required
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <label className="form-label text-xs mb-1">Data fim</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={novoBloco.endDate}
                                                min={novoBloco.startDate || today}
                                                onChange={e => setNovoBloco(p => ({ ...p, endDate: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <label className="form-label text-xs mb-1">Turno</label>
                                            <select
                                                className="form-control form-control-sm"
                                                value={novoBloco.shift}
                                                onChange={e => {
                                                    const s = Number(e.target.value);
                                                    setNovoBloco(p => ({
                                                        ...p,
                                                        shift: s,
                                                        startTime: s === 0 ? '08:00' : s === 1 ? '14:00' : '',
                                                        endTime: s === 0 ? '12:00' : s === 1 ? '18:00' : '',
                                                    }));
                                                }}
                                            >
                                                <option value={0}>Manhã</option>
                                                <option value={1}>Tarde</option>
                                                <option value={2}>Dia inteiro</option>
                                            </select>
                                        </div>
                                        {Number(novoBloco.shift) !== 2 && (
                                            <>
                                                <div className="col-auto">
                                                    <label className="form-label text-xs mb-1">Início</label>
                                                    <input
                                                        type="time"
                                                        className="form-control form-control-sm"
                                                        value={novoBloco.startTime}
                                                        min={Number(novoBloco.shift) === 1 ? '12:01' : '00:00'}
                                                        max={Number(novoBloco.shift) === 0 ? '12:00' : '23:59'}
                                                        onChange={e => setNovoBloco(p => ({ ...p, startTime: e.target.value }))}
                                                    />
                                                </div>
                                                <div className="col-auto">
                                                    <label className="form-label text-xs mb-1">Fim</label>
                                                    <input
                                                        type="time"
                                                        className="form-control form-control-sm"
                                                        value={novoBloco.endTime}
                                                        min={Number(novoBloco.shift) === 1 ? '12:01' : '00:00'}
                                                        max={Number(novoBloco.shift) === 0 ? '12:00' : '23:59'}
                                                        onChange={e => setNovoBloco(p => ({ ...p, endTime: e.target.value }))}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        <div className="col">
                                            <label className="form-label text-xs mb-1">Motivo (opcional)</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Ex: Reunião, Feriado..."
                                                value={novoBloco.reason}
                                                onChange={e => setNovoBloco(p => ({ ...p, reason: e.target.value }))}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <button type="submit" className="btn btn-primary btn-sm" disabled={savingBloco}>
                                                {savingBloco ? 'Salvando...' : 'Salvar'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}

                            {visibleBlocks.length === 0 && !showAllBlocks ? (
                                <p className="text-sm text-secondary mb-0">Nenhum bloqueio futuro cadastrado.</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Período</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Turno</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Motivo</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleBlocks.map(b => {
                                                    const isPast = b.endDate < today;
                                                    const isSingleDay = b.startDate === b.endDate;
                                                    return (
                                                        <tr key={b.id} style={{ opacity: isPast ? 0.5 : 1 }}>
                                                            <td className="text-xs">
                                                                {isPast && <i className="bi bi-clock-history text-secondary me-1" title="Passado"></i>}
                                                                {isSingleDay
                                                                    ? formatFullDate(b.startDate)
                                                                    : `${formatFullDate(b.startDate)} – ${formatFullDate(b.endDate)}`
                                                                }
                                                            </td>
                                                            <td className="text-xs">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    <span className={`badge ${b.shift === 2 ? 'bg-secondary' : ''}`} style={b.shift === 0 ? { background: '#86efac', color: '#166534' } : b.shift === 1 ? { background: '#16a34a', color: '#fff' } : {}}>
                                                                        {SHIFT_LABELS[b.shift]}
                                                                    </span>
                                                                    {b.startTime && b.endTime && (
                                                                        <span className="text-secondary">{fmt(b.startTime)}–{fmt(b.endTime)}</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="text-xs text-secondary">{b.reason || '—'}</td>
                                                            <td className="text-end">
                                                                {confirmDeleteBlockId === b.id ? (
                                                                    <div className="d-flex align-items-center gap-1 justify-content-end">
                                                                        <span className="text-xs text-secondary">Excluir?</span>
                                                                        <button
                                                                            className="btn btn-danger btn-sm py-0 px-2"
                                                                            onClick={() => { excluirBloco(b.id); setConfirmDeleteBlockId(null); }}
                                                                        >
                                                                            Sim
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-outline-secondary btn-sm py-0 px-2"
                                                                            onClick={() => setConfirmDeleteBlockId(null)}
                                                                        >
                                                                            Não
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        className="btn btn-link p-0"
                                                                        title="Remover"
                                                                        onClick={() => setConfirmDeleteBlockId(b.id)}
                                                                    >
                                                                        <i className="bi bi-trash text-danger"></i>
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                            )}

                            {pastBlocks.length > 0 && (
                                <button
                                    className="btn btn-link btn-sm text-secondary p-0 mt-2"
                                    onClick={() => setShowAllBlocks(v => !v)}
                                >
                                    <i className={`bi ${showAllBlocks ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                                    {showAllBlocks
                                        ? 'Ocultar bloqueios passados'
                                        : `Ver ${pastBlocks.length} bloqueio${pastBlocks.length > 1 ? 's' : ''} passado${pastBlocks.length > 1 ? 's' : ''}`
                                    }
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}

            <Footer />
        </div>
    );
};

export default EscalaTerapeuta;
